import React from 'react';
import { useTheme } from '@mui/material';
import { Link as RRLink, useHistory } from 'react-router-dom';
import { Box, Divider, Flex, Icon, TextInput } from 'pounceV2';
import useAuth from 'Hooks/useAuth';
import useFeatureToggle from 'Hooks/useFeatureToggle';
import useAIEnabled from 'Hooks/useAIEnabled';
import { useAppConfig } from 'Components/utils/AppBootstrapper/AppConfigContext';
import { expandPermissions } from 'Helpers/utils';
import urls from 'Source/urls';
import { SettingsGroup, SettingsItem, SETTINGS_GROUPS, SETTINGS_SIDEBAR_WIDTH } from '../constants';
import SettingsSidebarGroupV3 from './SettingsSidebarGroupV3';

interface SettingsSidebarV3Props {
  /** Item id resolved from the current URL (or null for "no match"). */
  activeItemId: string | null;
}

const SettingsSidebarV3: React.FC<SettingsSidebarV3Props> = ({ activeItemId }) => {
  const {
    palette: { tokens },
  } = useTheme();
  const { userInfo } = useAuth();
  const [query, setQuery] = React.useState('');
  const history = useHistory();

  // Captured on first render to survive internal settings navigation:
  // the sidebar lives outside the route Switch so this ref is reset only
  // when the user actually leaves `/settings/*`.
  const entryHistoryLength = React.useRef(history.length);

  const handleBack = React.useCallback(
    (event: React.MouseEvent) => {
      // Cold-loaded settings — no pre-settings entry to return to. Let
      // the RRLink default push to the dashboard run instead of calling
      // `history.go` into a blank prior origin.
      if (entryHistoryLength.current <= 1) {
        return;
      }
      event.preventDefault();
      const delta = history.length - entryHistoryLength.current + 1;
      history.go(-delta);
    },
    [history],
  );

  // Expand once per render so the per-item visibility check is a set
  // lookup — avoids calling `useCheckPermissions` inside a loop.
  const grantedPermissions = React.useMemo(
    () => new Set(expandPermissions(userInfo?.permissions ?? [])),
    [userInfo?.permissions],
  );

  const enableAISkills = useFeatureToggle('ENABLE_AI_SKILLS');
  const enableUsageDashboard = useFeatureToggle('USAGE_DASHBOARD');
  const enableInfraCostDashboard = useFeatureToggle('INFRA_COST_DASHBOARD');
  const aiEnabled = useAIEnabled();
  const { remoteMcpEnabled } = useAppConfig();

  const visibleGroups = React.useMemo<
    Array<{ group: SettingsGroup; items: SettingsItem[] }>
  >(() => {
    const normalized = query.trim().toLowerCase();
    const flagEnabled: Record<string, boolean> = {
      ENABLE_AI_SKILLS: enableAISkills,
      USAGE_DASHBOARD: enableUsageDashboard,
      INFRA_COST_DASHBOARD: enableInfraCostDashboard,
    };
    return SETTINGS_GROUPS.map(group => {
      const items = group.items.filter(item => {
        if (item.featureFlag && !flagEnabled[item.featureFlag]) {
          return false;
        }
        if (item.requiresAIEnabled && !aiEnabled) {
          return false;
        }
        if (item.requiresRemoteMcp && !remoteMcpEnabled) {
          return false;
        }
        // Empty `permissions` means visible to everyone.
        if (item.permissions.length > 0 && !item.permissions.some(p => grantedPermissions.has(p))) {
          return false;
        }
        return !normalized || item.label.toLowerCase().includes(normalized);
      });
      return { group, items };
    }).filter(entry => entry.items.length > 0);
  }, [
    grantedPermissions,
    query,
    enableAISkills,
    enableUsageDashboard,
    enableInfraCostDashboard,
    aiEnabled,
    remoteMcpEnabled,
  ]);

  return (
    <Flex
      as="aside"
      direction="column"
      width={SETTINGS_SIDEBAR_WIDTH}
      flexShrink={0}
      height="100vh"
      // Inline the colour in the shorthand so the value travels with the
      // border declaration. `borderColor` as a separate prop is ignored by
      // the `borderRight: "1px solid"` shorthand (which falls back to
      // `currentColor`) and would render a different shade than the
      // horizontal dividers, which use the same token via `<Divider />`.
      borderRight={`1px solid ${tokens.stroke.neutral[5]}`}
      backgroundColor={tokens.background.neutral.canvas.base}
      data-tid="settings-sidebar"
    >
      <Flex align="center" px={4} pt={4} pb={2}>
        <Box
          as={RRLink}
          to={urls.dashboard.home()}
          onClick={handleBack}
          aria-label="Back to Panther"
          data-tid="settings-sidebar-back"
          display="inline-flex"
          alignItems="center"
          color={tokens.content.text.neutral[2]}
          sx={{
            '&:hover': { color: tokens.content.text.neutral[1] },
          }}
        >
          <Icon type="arrow-left" size="small" />
          <Box as="span" ml={2} fontSize="small" fontWeight="medium">
            Back to Panther
          </Box>
        </Box>
      </Flex>
      <Box px={4} pt={2} pb={3}>
        <TextInput
          label="Search Settings"
          icon="search"
          iconAlignment="left"
          value={query}
          onChange={event => setQuery(event.target.value)}
          data-tid="settings-sidebar-search"
        />
      </Box>
      <Flex direction="column" gap={2} overflow="auto" pb={6}>
        {visibleGroups.map((entry, index) => (
          <React.Fragment key={entry.group.id}>
            {index > 0 && <Divider margin={0} mx={4} color={tokens.stroke.neutral[5]} />}
            <SettingsSidebarGroupV3
              group={entry.group}
              items={entry.items}
              activeItemId={activeItemId}
            />
          </React.Fragment>
        ))}
      </Flex>
    </Flex>
  );
};

export default React.memo(SettingsSidebarV3);
